import { Link } from 'react-router-dom';
import { Badge, Button, Group, Stack, Text, Title } from '@repletoryreach/sdk-ui';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import type { AuditRow } from '../../../data/useAuditFeed';
import { TenantName } from '../../../components/TenantName';
import { CopyableId } from '../../../components/CopyableId';
import { formatDateTime, formatRelative } from '../../../lib/format';
import { useT } from '../../../i18n/I18nProvider';
import { SOURCE_COLOR, SOURCE_LABEL_KEY } from './sourceColors';
import './audit-timeline.css';

const MUTED = 'var(--rr-color-text-muted)';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Group align="flex-start" justify="space-between" gap="md" wrap="nowrap">
      <Text role="caption" c={MUTED} style={{ flexShrink: 0 }}>{label}</Text>
      <div style={{ textAlign: 'right', minWidth: 0 }}>{children}</div>
    </Group>
  );
}

/**
 * One event, opened from the timeline.
 *
 * There is no per-event read behind this: the five feeds return everything the
 * console will ever know about a row, so the panel shows that record rather
 * than implying a fetch that would carry an IP address, a user agent or a
 * payload nobody stores. The id is kept because it is the value support asks a
 * customer to quote.
 */
export function AuditDetailDrawer({ row, onClose, onPrevious, onNext }: {
  row: AuditRow | null;
  onClose: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
}) {
  const t = useT();

  async function copyJson() {
    if (!row) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(row.raw, null, 2));
      notifySuccess(t('common.copied'));
    } catch {
      // A denied clipboard is not worth an error toast: the text is on screen.
    }
  }

  return (
    <Drawer opened={row !== null} onClose={onClose} title={t('audit.detail.title')} size="lg">
      {row && (
        <Stack gap="lg">
          <Group justify="space-between" align="center" wrap="nowrap">
            <Group gap="sm" align="center" wrap="nowrap">
              <Badge size="sm" color={SOURCE_COLOR[row.source]} variant="light">
                {t(SOURCE_LABEL_KEY[row.source])}
              </Badge>
              <Title order={5}>{row.summary}</Title>
            </Group>
          </Group>

          <Stack gap="sm">
            <Field label={t('audit.detail.occurred')}>
              <Text role="bodySmall" fw={600}>{formatDateTime(row.occurred_at)}</Text>
              <Text role="caption" c={MUTED}>{formatRelative(row.occurred_at)}</Text>
            </Field>
            <Field label={t('audit.detail.organisation')}>
              <TenantName id={row.organisation_id} />
            </Field>
            <Field label={t('audit.detail.eventId')}>
              <CopyableId value={row.id} />
            </Field>
            {row.detail && (
              <Field label={t('audit.columns.detail')}>
                <Text role="bodySmall">{row.detail}</Text>
              </Field>
            )}
          </Stack>

          <Stack gap="xs">
            <Group justify="space-between" align="center">
              <Text role="bodySmall" fw={600}>{t('audit.detail.record')}</Text>
              <Button size="xs" variant="default" onClick={copyJson}>{t('audit.detail.copyJson')}</Button>
            </Group>
            <pre className="rr-audit-json">{JSON.stringify(row.raw, null, 2)}</pre>
            <Text role="caption" c={MUTED}>{t('audit.detail.recordHint')}</Text>
          </Stack>

          <Link to={`/tenants/${row.organisation_id}`} className="rr-audit-drawer-link">
            {t('audit.detail.viewTenant')} <span aria-hidden>→</span>
          </Link>

          {(onPrevious || onNext) && (
            <Group justify="space-between" align="center">
              <Button size="xs" variant="subtle" onClick={onPrevious} disabled={!onPrevious}>
                {t('audit.detail.previous')}
              </Button>
              <Button size="xs" variant="subtle" onClick={onNext} disabled={!onNext}>
                {t('audit.detail.next')}
              </Button>
            </Group>
          )}
        </Stack>
      )}
    </Drawer>
  );
}
